/**
 * Seal tomorrow's case: once the agent has said well done and they have
 * picked when the next mission comes, they choose tomorrow's door now
 * (titles only: the blind choice stays blind) and the case is sealed with
 * it. Leaving with a case sealed is what brings them back: the morning text
 * names the door they chose, and HQ shows the case waiting. Over the
 * agent's screen, in the login modal's colours like the schedule picker.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { Door } from "@/features/funnel/types";
import { useDailyDoors } from "@/features/funnel/api/use-doors";
import { ACTION_BTN_CLASS } from "./step-shell";
import { SealedCase } from "./sealed-case";
import { unsealText } from "../comeback-texts";
import { whenLabel } from "./seal-time";

const GOLD = "#d6a94e";
/** The doors' colours, as on the hallway. */
const DOOR_COLOR: Record<Door, string> = {
  love: "#8fbfe8",
  stranger: "#e8b74e",
};
/** Without a server-sent pair, the two families, as the first doors read. */
const FALLBACK = [
  { family: "love" as Door, label: "Someone you love" },
  { family: "stranger" as Door, label: "Someone you barely know" },
];

export function SealTomorrow({
  name,
  at,
  onSealed,
  onChange,
}: {
  /** the operative, for the text's preview */
  name: string;
  /** when it opens (ms epoch): the time they picked, or tomorrow morning */
  at: number;
  /** sealed and confirmed: on to HQ */
  onSealed: (sealed: { door: Door; label: string; at: number }) => void;
  /** back to the choice of when */
  onChange: () => void;
}) {
  const { data: doors } = useDailyDoors();
  const pair = doors?.next?.length === 2 ? doors.next : FALLBACK;
  const [door, setDoor] = useState<Door | null>(null);
  const [sealed, setSealed] = useState(false);
  // the seal is pressed and held, like the stamp: let go early and it lifts
  const HOLD_MS = 700;
  const [holding, setHolding] = useState(false);
  const holdTimer = useRef(0);
  const startHold = () => {
    if (!door || sealed) return;
    setHolding(true);
    holdTimer.current = window.setTimeout(() => {
      setHolding(false);
      setSealed(true);
      try {
        navigator.vibrate?.([20, 30, 80]);
      } catch {
        /* no vibration here */
      }
    }, HOLD_MS);
  };
  const endHold = () => {
    window.clearTimeout(holdTimer.current);
    setHolding(false);
  };
  useEffect(() => () => window.clearTimeout(holdTimer.current), []);
  const chosen = pair.find((d) => d.family === door) ?? null;
  // Escape goes back, as Change does
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onChange();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onChange]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sm-seal-title"
      style={
        {
          "--sm-accent-edge": `${GOLD}6b`,
          "--sm-accent-strong": `${GOLD}80`,
          "--sm-btn-text": "#e6d3a4",
        } as CSSProperties
      }
      className="fixed inset-0 z-[70] grid animate-[smFadeIn_.2s_ease_backwards] place-items-center bg-black/75 p-4 font-sans"
    >
      <div className="max-h-full w-full max-w-[360px] animate-[smSay_.3s_cubic-bezier(.2,.8,.3,1)_backwards] overflow-y-auto rounded-xl border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18/0.97)] px-[22px] pt-[20px] pb-[18px] shadow-[0_20px_60px_rgba(0,0,0,.55)]">
        <div className="font-mono text-[10.5px] tracking-[.24em] text-[#d6a94e] uppercase">
          ▸ {sealed ? "Created" : "Tomorrow's case"}
        </div>
        <h2
          id="sm-seal-title"
          className="mt-2 font-stencil text-[22px] leading-[1.1] tracking-[.03em] text-[#f4eddb] uppercase"
        >
          {sealed ? "It's on your desk." : "Create tomorrow's case now."}
        </h2>

        {!sealed ? (
          <>
            <p className="mt-2 text-[15px] leading-snug text-[#ddd3bf]">
              It opens {whenLabel(at)}. Pick the door, and the case is created
              with it. You'll get one text when it's ready.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              {pair.map((d) => {
                const on = door === d.family;
                return (
                  <button
                    key={d.family}
                    type="button"
                    aria-pressed={on}
                    aria-label={`${d.family === "love" ? "Door one" : "Door two"}: ${d.label}`}
                    onClick={() => setDoor(d.family)}
                    style={
                      { "--sm-door": DOOR_COLOR[d.family] } as CSSProperties
                    }
                    className={`flex min-h-[112px] cursor-pointer flex-col justify-end rounded-[10px] border p-3 text-left transition-[border-color,box-shadow,translate] duration-200 ${
                      on
                        ? "border-[var(--sm-door)] bg-[color-mix(in_srgb,var(--sm-door)_16%,#0b0a09)] shadow-[0_0_22px_color-mix(in_srgb,var(--sm-door)_40%,transparent)] -translate-y-0.5"
                        : "border-[#d6a94e]/30 bg-black/40 hover:border-[#d6a94e]/60"
                    }`}
                  >
                    <span className="font-mono text-[9.5px] tracking-[.2em] text-[#a99d86] uppercase">
                      {d.family === "love" ? "Door one" : "Door two"}
                    </span>
                    <span className="mt-1 font-sans text-[14.5px] leading-[1.25] font-extrabold tracking-[.02em] text-[color-mix(in_srgb,var(--sm-door)_75%,white)] uppercase [text-shadow:0_0_10px_color-mix(in_srgb,var(--sm-door)_55%,transparent)]">
                      {d.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              disabled={!door}
              onPointerDown={(e) => {
                e.preventDefault();
                startHold();
              }}
              onPointerUp={endHold}
              onPointerCancel={endHold}
              onPointerLeave={endHold}
              onKeyDown={(e) => {
                if ((e.key === " " || e.key === "Enter") && !e.repeat)
                  startHold();
              }}
              onKeyUp={endHold}
              onContextMenu={(e) => e.preventDefault()}
              style={{ "--hold": `${HOLD_MS}ms` } as CSSProperties}
              className={`relative mt-4 w-full ${ACTION_BTN_CLASS} overflow-hidden rounded-lg px-4 py-3 text-[15px] font-semibold select-none [touch-action:none] ${
                holding ? "sm-seal-holding" : ""
              }`}
            >
              {/* the wax filling as they hold */}
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 bg-[#d6a94e]/35 ${
                  holding ? "sm-seal-fill" : "w-0"
                }`}
              />
              <span className="relative">
                {!door
                  ? "Pick a door"
                  : holding
                    ? "Hold it…"
                    : "Press and hold to create"}
              </span>
            </button>
            <button
              type="button"
              onClick={onChange}
              className="mt-2 w-full cursor-pointer rounded-lg px-4 py-2.5 text-[14.5px] font-medium text-[#a99d86]"
            >
              Change when it comes
            </button>
          </>
        ) : chosen ? (
          <>
            <div className="mt-3">
              <SealedCase label={chosen.label} at={at} />
            </div>
            {/* the one text that will come, as it will read */}
            <div className="mt-4 rounded-[10px] border border-[#d6a94e]/25 bg-black/40 p-3">
              <div className="font-mono text-[9.5px] tracking-[.2em] text-[#a99d86] uppercase">
                Your text · {whenLabel(at)}
              </div>
              <p className="mt-1.5 text-[14px] leading-[1.4] text-[#ddd3bf]">
                {unsealText(name, chosen.label)}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                onSealed({ door: chosen.family, label: chosen.label, at })
              }
              className={`mt-4 w-full ${ACTION_BTN_CLASS} rounded-lg px-4 py-3 text-[15px] font-semibold`}
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setSealed(false)}
              className="mt-2 w-full cursor-pointer rounded-lg px-4 py-2.5 text-[14.5px] font-medium text-[#a99d86]"
            >
              Change the door
            </button>
          </>
        ) : null}
      </div>
      <style>{`
        .sm-seal-fill{animation:smSealFill var(--hold) linear both}
        .sm-seal-holding{scale:.97;transition:scale .15s}
        @keyframes smSealFill{from{width:0}to{width:100%}}
        @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes smSay{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}
      `}</style>
    </div>
  );
}
